import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Monitor, ArrowRight, CheckCircle, ShieldCheck, ArrowDownUpIcon, Factory, Cpu, Hammer, Flame, Hotel } from 'lucide-react';
import { services } from '../data/services';
import { Award, Target } from 'lucide-react';

const iconMap = {
  ShieldCheck,
  Users,
  ArrowDownUpIcon,
  Monitor
};


const Services = () => {

  const areas = [
    {
      icon: Factory,
      title: "MANUFACTURING",
    },
    {
      icon: Cpu,
      title: "TECHNOLOGY",
    },
    {
      icon: Hammer,
      title: "CONSTRUCTION",
    },
    {
      icon: Flame,
      title: "OIL & GAS",
    },
    {
      icon: Hotel,
      title: "HOSPITALITY",
    }
  ];

  return (
    <section id="services" className= "py-5 bg-gradient-to-br from-gray-50 to-white scroll-mt-40">
      <div className="container mx-auto px-4">

        {/* Areas */}

        <motion.div
          className="mt-20"
          initial={{ opacity: 0, y: 80 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {areas.map((value, index) => {
              const IconComponent = value.icon;

              return (
                <motion.div
                  key={index}
                  className="text-center group"
                  initial={
                    index === 0 ? { opacity: 0, x: -100 } :
                    index === 1 ? { opacity: 0, y: -100 } :
                    index === 2 ? { opacity: 0, scale: 0 } :
                    index === 3 ? { opacity: 0, x: 100 } :
                    { opacity: 0, y: 100 }
                  }
                  whileInView={
                    index === 0 ? { opacity: 1, x: 0 } :
                    index === 1 ? { opacity: 1, y: 0 } :
                    index === 2 ? { opacity: 1, scale: 1 } :
                    index === 3 ? { opacity: 1, x: 0 } :
                    { opacity: 1, y: 0 }
                  }
                  transition={{ delay: index * 0.1, duration: 1.2 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 border-t border-l border-white/30 border-b border-r border-black/20 shadow-sm"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </motion.div>

                  <h4 className="text-2xl md:text-4xl font-semibold text-gray-700 mb-3">{value.title}</h4>

                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Header */}

        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Shield className="w-4 h-4" />
            <span className="font-semibold">Our Servicess</span>
          </motion.div>

          <h2 className="text-4xl lg:text-4xl font-bold text-gray-900 mb-6">
            Training and Services
            <span className="block bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              for Employers and Their Workforce in English and Spanish
            </span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            “Our industry consultants work hand in hand with you to deliver everything needed to upskill and strengthen your workforce.”
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-4 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];

            return (
              <motion.div
                key={service.id}
                className="flex flex-col h-full group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                {/* Icon */}
                <motion.div
                  className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <IconComponent className="w-8 h-8 text-white" />
                </motion.div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors">
                  {service.title}
                </h3>

                <p className="text-gray-600 mb-8 leading-relaxed h-[210px]">
                  {service.description}
                </p>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {service.features.map((feature, featureIndex) => (
                    <motion.div
                      key={featureIndex}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: (index * 0.1) + (featureIndex * 0.05) + 0.3 }}
                      viewport={{ once: true }}
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <motion.button
                  className="flex items-center gap-3 text-orange-600 font-bold hover:text-orange-700 transition-colors group-hover:gap-3 mt-auto "
                  whileHover={{ x: 10 }}
                >
                  <a href="mailto:info@mdxsafetyinc.com" class="flex items-center gap-2 text-orange ">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <span class="font-semibold">Learn More</span>
                  </a>
                  <ArrowRight className="w-8 h-8" />
                </motion.button>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
        >
          <motion.button
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a href="mailto:info@mdxsafetyinc.com" class="flex items-center gap-2 text-white hover:text-[#a1a09a] transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              <span class="font-semibold">Get your solution Now</span>
            </a>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;